import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { sendTestEmail, validateEmail } from '../../services/smtpService';
import { SMTPConfig } from '../../types/admin';
import './EmailTestModal.css';

interface EmailTestModalProps {
	config: SMTPConfig;
	isOpen: boolean;
	onClose: () => void;
}

export const EmailTestModal: React.FC<EmailTestModalProps> = ({ config, isOpen, onClose }) => {
	const { token } = useAuth();
	const [sending, setSending] = useState(false);
	const [testEmail, setTestEmail] = useState('');
	const [error, setError] = useState<string>('');
	const [success, setSuccess] = useState<string>('');
	const [emailError, setEmailError] = useState<string>('');

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const email = e.target.value;
		setTestEmail(email);

		// Clear previous errors
		setEmailError('');
		setError('');
		setSuccess('');

		// Validate email format
		if (email && !validateEmail(email)) {
			setEmailError('Molimo unesite validnu email adresu');
		}
	};

	const handleSendTest = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!testEmail.trim()) {
			setEmailError('Molimo unesite email adresu');
			return;
		}

		if (!validateEmail(testEmail)) {
			setEmailError('Molimo unesite validnu email adresu');
			return;
		}

		setSending(true);
		setError('');
		setSuccess('');

		try {
			const result = await sendTestEmail(token!, config.id, testEmail);

			if (result.success) {
				setSuccess(`Test email uspešno poslat! ID poruke: ${result.messageId || 'N/A'}`);
				setTestEmail(''); // Clear the form on success
			} else {
				setError(result.error || 'Neuspešno slanje test email-a');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Neuspešno slanje test email-a');
		} finally {
			setSending(false);
		}
	};

	const handleClose = () => {
		setTestEmail('');
		setError('');
		setSuccess('');
		setEmailError('');
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div className='modal-overlay'>
			<div className='modal-content email-test-modal'>
				<div className='modal-header'>
					<h3>Pošalji test email</h3>
					<button className='close-btn' onClick={handleClose}>
						&times;
					</button>
				</div>

				<div className='modal-body'>
					{/* Configuration Info */}
					<div className='config-info'>
						<h4>SMTP Konfiguracija: {config.name}</h4>
						<div className='config-details'>
							<div className='detail-row'>
								<span className='label'>Host:</span>
								<span className='value'>
									{config.host}:{config.port}
								</span>
							</div>
							<div className='detail-row'>
								<span className='label'>Od:</span>
								<span className='value'>{config.from_name ? `${config.from_name} <${config.from_email}>` : config.from_email}</span>
							</div>
						</div>
					</div>

					{/* Email Form */}
					<form onSubmit={handleSendTest} className='email-test-form'>
						<div className='form-group'>
							<label htmlFor='testEmail'>Email adresa primaoca *</label>
							<input type='email' id='testEmail' value={testEmail} onChange={handleEmailChange} className={`form-control ${emailError ? 'error' : ''}`} placeholder='Unesite email adresu za slanje test email-a...' disabled={sending} required />
							{emailError && <span className='error-text'>{emailError}</span>}
						</div>

						{/* Test Email Preview */}
						<div className='email-preview'>
							<h4>Prikaz Test email-a</h4>
							<div className='preview-content'>
								<div className='preview-row'>
									<strong>Subjekt:</strong> SMTP Test email
								</div>
								<div className='preview-row'>
									<strong>Od:</strong> {config.from_name ? `${config.from_name} <${config.from_email}>` : config.from_email}
								</div>
								<div className='preview-row'>
									<strong>Za:</strong> {testEmail || 'primalac@example.com'}
								</div>
								<div className='preview-content-body'>
									<strong>Sadržaj:</strong>
									<div className='preview-text'>
										Ovo je test email za verifikaciju vaše SMTP konfiguracije.
										<br />
										<br />
										Konfiguracija: {config.name}
										<br />
										Poslato u: {new Date().toLocaleString()}
										<br />
										<br />
										Ako ste primili ovaj email, vaša SMTP konfiguracija funkcioniše ispravno!
									</div>
								</div>
							</div>
						</div>

						{/* Messages */}
						{error && <div className='error-message'>{error}</div>}
						{success && <div className='success-message'>{success}</div>}

						{/* Form Actions */}
						<div className='form-actions'>
							<button type='button' onClick={handleClose} className='btn btn-secondary' disabled={sending}>
								Otkaži
							</button>
							<button type='submit' className='btn btn-primary' disabled={sending || !!emailError || !testEmail.trim()}>
								{sending ? (
									<>
										<span className='spinner'></span>
										Slanje test email-a...
									</>
								) : (
									'Pošalji test email'
								)}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};
